import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Truck, 
  Users, 
  Wrench, 
  Fuel, 
  TrendingUp, 
  TrendingDown,
  Activity,
  AlertTriangle,
  ClipboardList,
  Bell,
  BellRing,
  ChevronRight,
  CreditCard
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts";

const CHART_COLORS = {
  primary: "#4ade80",
  secondary: "#3b82f6",
  warning: "#fbbf24",
  error: "#ef4444"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatShortCurrency = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`;
  }
  return value.toString();
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [repairOrders, setRepairOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, expensesRes, vehiclesRes, ordersRes, alertsRes, alertsSummaryRes] = await Promise.all([
        apiClient.get("/analytics/dashboard"),
        apiClient.get("/analytics/monthly-expenses"),
        apiClient.get("/vehicles"),
        apiClient.get("/repair-orders"),
        apiClient.get("/alerts"),
        apiClient.get("/alerts/summary")
      ]);
      setStats(statsRes.data);
      setMonthlyExpenses(expensesRes.data);
      setVehicles(vehiclesRes.data);
      setRepairOrders(ordersRes.data);
      setAlerts(alertsRes.data.slice(0, 5)); // Get top 5 alerts
      setAlertsSummary(alertsSummaryRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const vehicleStatusData = vehicles.length > 0 ? [
    { name: "Active", value: vehicles.filter(v => v.status === "active").length, color: CHART_COLORS.primary },
    { name: "Maintenance", value: vehicles.filter(v => v.status === "maintenance").length, color: CHART_COLORS.warning },
    { name: "Out of Service", value: vehicles.filter(v => v.status === "out_of_service").length, color: CHART_COLORS.error }
  ].filter(d => d.value > 0) : [];

  const pendingRepairs = repairOrders.filter(o => o.status === "pending" || o.status === "in_progress").length;

  const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = "primary", onClick }) => (
    <Card 
      className={`bg-card border-border rounded-sm card-tactical relative overflow-hidden ${onClick ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`} 
      data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
    >
      <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-bl-full" />
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-heading font-bold text-foreground mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs font-mono text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-sm bg-${color}/10 border border-${color}/30 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-xs font-mono ${trend >= 0 ? "text-green-400" : "text-red-400"}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{Math.abs(trend)}% vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card border-border rounded-sm">
              <CardContent className="p-4">
                <div className="skeleton-tactical h-4 w-24 rounded mb-2" />
                <div className="skeleton-tactical h-8 w-16 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            OPERATIONS DASHBOARD
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Fleet status and analytics overview
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-sm">
          <Activity className="w-4 h-4 text-primary animate-pulse" />
          <span className="text-xs font-mono text-muted-foreground">REAL-TIME DATA</span>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <KPICard 
          title="Total Vehicles" 
          value={stats?.total_vehicles || 0}
          subtitle={`${stats?.active_vehicles || 0} active`}
          icon={Truck}
          onClick={() => navigate("/vehicles")}
        />
        <KPICard 
          title="Total Drivers" 
          value={stats?.total_drivers || 0}
          subtitle={`${stats?.available_drivers || 0} available`}
          icon={Users}
          onClick={() => navigate("/drivers")}
        />
        <KPICard 
          title="Active Alerts" 
          value={alertsSummary?.total || 0}
          subtitle={`${alertsSummary?.by_severity?.critical || 0} critical`}
          icon={Bell}
          onClick={() => navigate("/alerts")}
        />
        <KPICard 
          title="Maintenance Cost" 
          value={formatShortCurrency(stats?.total_maintenance_cost || 0)}
          subtitle="TZS this period"
          icon={Wrench}
          onClick={() => navigate("/service-history")}
        />
        <KPICard 
          title="Fuel Cost" 
          value={formatShortCurrency(stats?.total_fuel_cost || 0)}
          subtitle="TZS this period"
          icon={Fuel}
          onClick={() => navigate("/fuel-records")}
        />
        <KPICard 
          title="Repair Orders" 
          value={pendingRepairs}
          subtitle="pending/in progress"
          icon={ClipboardList}
          onClick={() => navigate("/repair-orders")}
        />
      </div>

      {/* Alerts */}
      {stats?.vehicles_in_maintenance > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30 rounded-sm" data-testid="maintenance-alert">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-sm font-medium text-yellow-400">Maintenance Alert</p>
              <p className="text-xs font-mono text-yellow-400/70">
                {stats.vehicles_in_maintenance} vehicle(s) currently in maintenance
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Critical Alerts Banner */}
      {alertsSummary?.by_severity?.critical > 0 && (
        <Card 
          className="bg-red-500/10 border-red-500/30 rounded-sm cursor-pointer hover:border-red-500/50 transition-colors" 
          data-testid="critical-alerts-banner"
          onClick={() => navigate("/alerts")}
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-400 animate-pulse" />
              <div>
                <p className="text-sm font-heading text-red-400 tracking-wide">CRITICAL ALERTS DETECTED</p>
                <p className="text-xs font-mono text-red-400/70">
                  {alertsSummary.by_severity.critical} critical alert(s) require immediate attention
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-red-400" />
          </CardContent>
        </Card>
      )}

      {/* Alerts Widget */}
      {alerts.length > 0 && (
        <Card className="bg-card border-border rounded-sm" data-testid="alerts-widget">
          <CardHeader className="border-b border-border pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" />
              RECENT ALERTS
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/alerts")}
              className="font-mono text-xs hover:bg-primary/10"
              data-testid="view-all-alerts-btn"
            >
              View All <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {alerts.map((alert) => {
                const severityColors = {
                  critical: "text-red-400 bg-red-500/10",
                  high: "text-orange-400 bg-orange-500/10",
                  medium: "text-yellow-400 bg-yellow-500/10",
                  low: "text-blue-400 bg-blue-500/10"
                };
                const AlertIcon = alert.severity === "critical" ? AlertTriangle : 
                                  alert.severity === "high" ? BellRing : Bell;
                const EntityIcon = alert.entity_type === "vehicle" ? Truck : 
                                   alert.alert_type.includes("license") ? CreditCard : Users;
                
                return (
                  <div 
                    key={alert.id} 
                    className="p-3 flex items-center gap-3 hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => navigate("/alerts")}
                    data-testid={`dashboard-alert-${alert.id}`}
                  >
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${severityColors[alert.severity]}`}>
                      <AlertIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">{alert.title}</p>
                        <Badge className={`${severityColors[alert.severity]} border-0 rounded-sm font-mono text-[10px] px-1.5`}>
                          {alert.severity.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <EntityIcon className="w-3 h-3 text-muted-foreground" />
                        <p className="text-xs font-mono text-muted-foreground truncate">{alert.entity_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs font-mono font-bold ${
                        alert.days_remaining !== null && alert.days_remaining < 0 
                          ? "text-red-400" 
                          : alert.days_remaining !== null && alert.days_remaining <= 7 
                            ? "text-orange-400" 
                            : "text-muted-foreground"
                      }`}>
                        {alert.days_remaining !== null 
                          ? alert.days_remaining < 0 
                            ? `${Math.abs(alert.days_remaining)}d overdue`
                            : `${alert.days_remaining}d`
                          : "-"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Expense Trend Chart */}
        <Card className="bg-card border-border rounded-sm lg:col-span-2" data-testid="expense-chart">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground">
              MONTHLY EXPENSES TREND
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64 chart-container">
              {monthlyExpenses.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyExpenses}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(120 10% 18%)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="hsl(120 20% 55%)"
                      tick={{ fill: "hsl(120 20% 55%)", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    />
                    <YAxis 
                      stroke="hsl(120 20% 55%)"
                      tick={{ fill: "hsl(120 20% 55%)", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      tickFormatter={formatShortCurrency}
                    />
                    <Tooltip 
                      contentStyle={{
                        background: "hsl(120 10% 7%)",
                        border: "1px solid hsl(120 10% 18%)",
                        borderRadius: "4px",
                        fontFamily: "JetBrains Mono",
                        fontSize: "11px"
                      }}
                      formatter={(value) => [formatCurrency(value), ""]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="maintenance" 
                      name="Maintenance"
                      stroke={CHART_COLORS.warning} 
                      strokeWidth={2}
                      dot={{ fill: CHART_COLORS.warning, strokeWidth: 0, r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="fuel" 
                      name="Fuel"
                      stroke={CHART_COLORS.primary} 
                      strokeWidth={2}
                      dot={{ fill: CHART_COLORS.primary, strokeWidth: 0, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground font-mono text-sm">
                  No expense data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Status Pie Chart */}
        <Card className="bg-card border-border rounded-sm" data-testid="status-chart">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground">
              FLEET STATUS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64">
              {vehicleStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={vehicleStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {vehicleStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        background: "hsl(120 10% 7%)",
                        border: "1px solid hsl(120 10% 18%)",
                        borderRadius: "4px",
                        fontFamily: "JetBrains Mono",
                        fontSize: "11px"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground font-mono text-sm">
                  No vehicle data
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {vehicleStatusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: item.color }} />
                  <span className="text-xs font-mono text-muted-foreground">
                    {item.name}: {item.value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown Bar Chart */}
      <Card className="bg-card border-border rounded-sm" data-testid="monthly-breakdown-chart">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="font-heading text-sm tracking-wide text-foreground">
            MONTHLY COST BREAKDOWN
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-64 chart-container">
            {monthlyExpenses.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyExpenses}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(120 10% 18%)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="hsl(120 20% 55%)"
                    tick={{ fill: "hsl(120 20% 55%)", fontSize: 11, fontFamily: "JetBrains Mono" }}
                  />
                  <YAxis 
                    stroke="hsl(120 20% 55%)"
                    tick={{ fill: "hsl(120 20% 55%)", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    tickFormatter={formatShortCurrency}
                  />
                  <Tooltip 
                    contentStyle={{
                      background: "hsl(120 10% 7%)",
                      border: "1px solid hsl(120 10% 18%)",
                      borderRadius: "4px",
                      fontFamily: "JetBrains Mono",
                      fontSize: "11px"
                    }}
                    formatter={(value) => [formatCurrency(value), ""]}
                  />
                  <Legend />
                  <Bar 
                    dataKey="maintenance" 
                    name="Maintenance"
                    fill={CHART_COLORS.warning} 
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar 
                    dataKey="fuel" 
                    name="Fuel"
                    fill={CHART_COLORS.primary} 
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground font-mono text-sm">
                No expense data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
